import axios from 'axios';
import API_CONFIG from '../config/api.js';
import { announceServicePaused, CLIENT_APP_HEADER } from './systemStatusService.js';
import { 
  getErrorDetails, 
  ERROR_TYPES, 
  isOnline
} from '../utils/errorHandler.js';

// Create axios instance with interceptors
const api = axios.create({
  baseURL: API_CONFIG.BASE_URL,
  timeout: API_CONFIG.TIMEOUT,
  headers: API_CONFIG.DEFAULT_HEADERS,
});

// Request interceptor to add auth token and check network
api.interceptors.request.use(
  (config) => {
    // Check network connectivity before making request
    if (!isOnline()) {
      const error = new Error('No internet connection. Please check your network and try again.');
      error.code = 'ERR_NETWORK';
      error.isOffline = true;
      return Promise.reject(error);
    }
    
    // Tells the backend kill switch which surface this request belongs to
    config.headers['X-Client-App'] = CLIENT_APP_HEADER;

    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor with enhanced error handling
api.interceptors.response.use(
  (response) => {
    return response.data;
  },
  (error) => {
    // The super admin has paused the PMS — hand control to the maintenance
    // screen instead of surfacing a generic server error.
    if (error.response?.status === 503 && error.response?.data?.code === 'SERVICE_PAUSED') {
      announceServicePaused(error.response.data.maintenance);
      return Promise.reject({
        ...error.response.data,
        isServicePaused: true,
        response: error.response,
      });
    }

    // Get error details for better logging and handling
    const errorDetails = getErrorDetails(error);
    
    // Enhanced error logging
    console.error('API Error:', {
      type: errorDetails.type,
      title: errorDetails.title,
      url: error.config?.url,
      method: error.config?.method?.toUpperCase(),
      status: error.response?.status,
      message: errorDetails.message,
      isTimeout: errorDetails.type === ERROR_TYPES.TIMEOUT,
      isNetwork: errorDetails.type === ERROR_TYPES.NETWORK,
      isOffline: !isOnline()
    });

    // Create enhanced error object
    const enhancedError = {
      ...error,
      errorType: errorDetails.type,
      errorTitle: errorDetails.title,
      errorMessage: errorDetails.message,
      errorAction: errorDetails.action,
      isNetworkError: errorDetails.type === ERROR_TYPES.NETWORK,
      isTimeoutError: errorDetails.type === ERROR_TYPES.TIMEOUT,
      isAuthError: errorDetails.type === ERROR_TYPES.AUTH,
      isServerError: errorDetails.type === ERROR_TYPES.SERVER,
      canRetry: [ERROR_TYPES.NETWORK, ERROR_TYPES.TIMEOUT, ERROR_TYPES.SERVER].includes(errorDetails.type),
      originalError: error,
      response: error.response,
      // Preserve original error data
      error: error.response?.data?.error || errorDetails.message,
      message: error.response?.data?.message || errorDetails.message
    };
    
    return Promise.reject(enhancedError);
  }
);

// Auth Service
export const authService = {
  login: async (credentials) => {
    const response = await api.post('/api/auth/login', credentials);
    return response;
  },

  register: async (userData) => {
    const response = await api.post('/api/auth/register', userData);
    return response;
  },

  getCurrentUser: async () => {
    const response = await api.get('/api/auth/me');
    return response;
  },

  logout: async () => {
    await api.post('/api/auth/logout');
    localStorage.removeItem('token');
  },
};

// Properties Service
export const propertiesService = {
  getAll: async (params = {}) => {
    const response = await api.get('/api/properties', { params });
    return response;
  },

  getById: async (id) => {
    const response = await api.get(`/api/properties/${id}`);
    return response;
  },

  create: async (propertyData) => {
    const response = await api.post('/api/properties', propertyData);
    return response;
  },

  update: async (id, propertyData) => {
    const response = await api.put(`/api/properties/${id}`, propertyData);
    return response;
  },

  delete: async (id) => {
    await api.delete(`/api/properties/${id}`);
  },
};

// Rooms Service
export const roomsService = {
  getAll: async (params = {}) => {
    const response = await api.get('/api/rooms', { params });
    return response;
  },

  getById: async (id) => {
    const response = await api.get(`/api/rooms/${id}`);
    return response;
  },

  create: async (roomData) => {
    const response = await api.post('/api/rooms', roomData);
    return response;
  },

  update: async (id, roomData) => {
    const response = await api.put(`/api/rooms/${id}`, roomData);
    return response;
  },

  delete: async (id) => {
    await api.delete(`/api/rooms/${id}`);
  },

  addBed: async (roomId, bedData) => {
    const response = await api.post(`/api/rooms/${roomId}/beds`, bedData);
    return response;
  },

  updateBed: async (bedId, bedData) => {
    const response = await api.put(`/api/rooms/beds/${bedId}`, bedData);
    return response;
  },

  deleteBed: async (bedId) => {
    await api.delete(`/api/rooms/beds/${bedId}`);
  },

  // Room Types
  getRoomTypes: async () => {
    const response = await api.get('/api/rooms/types');
    return response;
  },

  getRoomTypeById: async (id) => {
    const response = await api.get(`/api/rooms/types/${id}`);
    return response;
  },

  createRoomType: async (roomTypeData) => {
    const response = await api.post('/api/rooms/types', roomTypeData);
    return response;
  },

  updateRoomType: async (id, roomTypeData) => {
    const response = await api.put(`/api/rooms/types/${id}`, roomTypeData);
    return response;
  },

  deleteRoomType: async (id) => {
    await api.delete(`/api/rooms/types/${id}`);
  },
};

// Courses Service
const COURSES_PAGE_SIZE = 100;

export const coursesService = {
  getAll: async (params = {}) => {
    const response = await api.get('/api/courses', { params });
    return response;
  },

  // /api/courses is paginated (defaults to 10 per page), so a single getAll call
  // only ever returns the first slice. This walks every page (100 per request)
  // and returns the combined list.
  getAllPages: async (params = {}) => {
    const limit = params.limit || COURSES_PAGE_SIZE;

    const firstResponse = await api.get('/api/courses', {
      params: { ...params, page: 1, limit },
    });

    const firstPage = firstResponse?.data || firstResponse || {};
    const firstCourses = firstPage.courses || [];
    const total = typeof firstPage.total === 'number' ? firstPage.total : firstCourses.length;
    const totalPages = Math.max(1, Math.ceil(total / limit));

    let allCourses = firstCourses;

    if (totalPages > 1) {
      const remainingPages = [];
      for (let page = 2; page <= totalPages; page++) {
        remainingPages.push(page);
      }

      const responses = await Promise.all(
        remainingPages.map((page) =>
          api.get('/api/courses', { params: { ...params, page, limit } })
        )
      );

      allCourses = responses.reduce((acc, response) => {
        const pageData = response?.data || response || {};
        return acc.concat(pageData.courses || []);
      }, [...firstCourses]);
    }

    // Guard against duplicates if a course is created/removed between page requests
    const seen = new Set();
    const uniqueCourses = allCourses.filter((course) => {
      if (!course || seen.has(course.id)) return false;
      seen.add(course.id);
      return true;
    });

    return { courses: uniqueCourses, total, totalPages, limit };
  },

  getById: async (id) => {
    const response = await api.get(`/api/courses/${id}`);
    return response;
  },

  create: async (courseData) => {
    const response = await api.post('/api/courses', courseData);
    return response;
  },

  update: async (id, courseData) => {
    const response = await api.put(`/api/courses/${id}`, courseData);
    return response;
  },

  delete: async (id) => {
    await api.delete(`/api/courses/${id}`);
  },
};

// Add-ons Service
export const addonsService = {
  getAll: async (params = {}) => {
    const response = await api.get('/api/addons', { params });
    return response;
  },

  getById: async (id) => {
    const response = await api.get(`/api/addons/${id}`);
    return response;
  },

  create: async (addonData) => {
    const response = await api.post('/api/addons', addonData);
    return response;
  },

  update: async (id, addonData) => {
    const response = await api.put(`/api/addons/${id}`, addonData);
    return response;
  },

  delete: async (id) => {
    await api.delete(`/api/addons/${id}`);
  },
};

// Booking Add-ons Service
export const bookingAddonsService = {
  getAll: async (params = {}) => {
    const response = await api.get('/api/booking-addons', { params });
    return response;
  },

  getById: async (id) => {
    const response = await api.get(`/api/booking-addons/${id}`);
    return response;
  },

  getByBooking: async (bookingId) => {
    const response = await api.get(`/api/booking-addons/booking/${bookingId}`);
    return response;
  },

  create: async (bookingAddonData) => {
    const response = await api.post('/api/booking-addons', bookingAddonData);
    return response;
  },

  createBulk: async (bulkData) => {
    const response = await api.post('/api/booking-addons/bulk', bulkData);
    return response;
  },

  update: async (id, bookingAddonData) => {
    const response = await api.put(`/api/booking-addons/${id}`, bookingAddonData);
    return response;
  },

  delete: async (id) => {
    await api.delete(`/api/booking-addons/${id}`);
  },
};

// Items Service (Inventory Management for AddOn Services)
export const itemsService = {
  // List all items with filtering and pagination
  getAll: async (params = {}) => {
    const response = await api.get('/api/items', { params });
    return response;
  },

  // Get single item with usage history
  getById: async (id) => {
    const response = await api.get(`/api/items/${id}`);
    return response;
  },

  // Get all items for a specific addon service
  getByService: async (addOnServiceId, params = {}) => {
    const response = await api.get(`/api/items/service/${addOnServiceId}`, { params });
    return response;
  },

  // Create new item
  create: async (itemData) => {
    const response = await api.post('/api/items', itemData);
    return response;
  },

  // Update item (excluding quantity)
  update: async (id, itemData) => {
    const response = await api.put(`/api/items/${id}`, itemData);
    return response;
  },

  // Adjust inventory (add or remove stock)
  adjustInventory: async (id, adjustmentData) => {
    const response = await api.post(`/api/items/${id}/adjust-inventory`, adjustmentData);
    return response;
  },

  // Delete item (soft delete by default, hard delete with permanent=true)
  delete: async (id, permanent = false) => {
    const response = await api.delete(`/api/items/${id}`, {
      params: { permanent }
    });
    return response;
  },

  // Get low stock report
  getLowStockReport: async (params = {}) => {
    const response = await api.get('/api/items/reports/low-stock', { params });
    return response;
  },
};

// Bookings Service
export const bookingsService = {
  getAll: async (params = {}) => {
    const response = await api.get('/api/bookings', { params });
    return response;
  },

  getById: async (id) => {
    const response = await api.get(`/api/bookings/${id}`);
    return response;
  },

  // Admin booking details endpoint
  getAdminBookingDetails: async (id) => {
    const response = await api.get(`/api/admin/booking-details/${id}`);
    return response;
  },

  create: async (bookingData) => {
    // Transform old format to new format for backward compatibility
    // If old format (roomId, bedId, courseId), convert to new format (rooms[], beds[], courses[])
    let requestData = bookingData;
    
    // Check if it's old format (has roomId, bedId, or courseId at root level)
    if (bookingData.roomId || bookingData.bedId || bookingData.courseId) {
      requestData = {
        userId: bookingData.userId,
        checkIn: bookingData.checkIn,
        checkOut: bookingData.checkOut,
        rooms: bookingData.roomId ? [{
          roomId: bookingData.roomId,
          checkIn: bookingData.checkIn,
          checkOut: bookingData.checkOut,
          notes: bookingData.roomNotes || null
        }] : [],
        beds: bookingData.bedId ? [{
          bedId: bookingData.bedId,
          checkIn: bookingData.checkIn,
          checkOut: bookingData.checkOut,
          notes: bookingData.bedNotes || null
        }] : [],
        courses: bookingData.courseId ? [{
          courseId: bookingData.courseId,
          quantity: bookingData.courseCount || 1,
          notes: bookingData.courseNotes || null
        }] : [],
        addOns: bookingData.addOns || [],
        notes: bookingData.notes || null,
        bookingPlatform: bookingData.bookingPlatform || 'BOOKING_ENGINE'
      };
    }
    
    const response = await api.post('/api/bookings', requestData);
    return response;
  },

  calculatePrice: async (priceData) => {
    // Transform old format to new format for backward compatibility
    // If old format (roomId, bedId, courseId), convert to new format (rooms[], beds[], courses[])
    let requestData = priceData;
    
    // Check if it's old format (has roomId, bedId, or courseId at root level)
    if (priceData.roomId || priceData.bedId || priceData.courseId) {
      requestData = {
        rooms: priceData.roomId ? [{
          roomId: priceData.roomId,
          checkIn: priceData.checkIn,
          checkOut: priceData.checkOut
        }] : [],
        beds: priceData.bedId ? [{
          bedId: priceData.bedId,
          checkIn: priceData.checkIn,
          checkOut: priceData.checkOut
        }] : [],
        courses: priceData.courseId ? [{
          courseId: priceData.courseId,
          quantity: priceData.courseCount || 1
        }] : [],
        addOns: priceData.addOns || [],
        isInterState: priceData.isInterState || false
      };
    }
    
    const response = await api.post('/api/bookings/calculate-price', requestData);
    return response;
  },

  update: async (id, bookingData) => {
    const response = await api.put(`/api/bookings/${id}`, bookingData);
    return response;
  },

  cancel: async (id) => {
    await api.delete(`/api/bookings/${id}`);
  },

  permanentDelete: async (id) => {
    const response = await api.delete(`/api/bookings/${id}/permanent-delete`);
    return response;
  },

  confirm: async (id) => {
    const response = await api.post(`/api/bookings/${id}/confirm`);
    return response;
  },

  checkIn: async (id) => {
    const response = await api.post(`/api/bookings/${id}/checkin`);
    return response;
  },

  checkOut: async (id) => {
    const response = await api.post(`/api/bookings/${id}/checkout`);
    return response;
  },

  earlyCheckOut: async (id, checkOutDate) => {
    const response = await api.post(`/api/bookings/${id}/early-checkout`, {
      checkOutDate
    });
    return response;
  },

  // Deallocate course from booking (convert to staycation)
  deallocateCourse: async (id) => {
    const response = await api.delete(`/api/bookings/${id}/deallocate-course`);
    return response;
  },

  // Staff reservation creation
  createStaffReservation: async (reservationData) => {
    // Transform old format to new format for backward compatibility
    // If old format (roomId, bedId, courseId), convert to new format (rooms[], beds[], courses[])
    let requestData = reservationData;
    
    // Check if it's old format (has roomId, bedId, or courseId at root level)
    if (reservationData.roomId || reservationData.bedId || reservationData.courseId) {
      // Handle guestDetails - if provided, create user or use existing
      const userData = reservationData.guestDetails || {};
      
      requestData = {
        userId: reservationData.userId || null, // Will be handled by backend if guestDetails provided
        guestDetails: userData.name ? userData : null,
        checkIn: reservationData.checkIn,
        checkOut: reservationData.checkOut,
        rooms: reservationData.roomId ? [{
          roomId: reservationData.roomId,
          checkIn: reservationData.checkIn,
          checkOut: reservationData.checkOut,
          notes: reservationData.roomNotes || null
        }] : [],
        beds: reservationData.bedId ? [{
          bedId: reservationData.bedId,
          checkIn: reservationData.checkIn,
          checkOut: reservationData.checkOut,
          notes: reservationData.bedNotes || null
        }] : [],
        courses: reservationData.courseId ? [{
          courseId: reservationData.courseId,
          quantity: reservationData.courseCount || 1,
          notes: reservationData.courseNotes || null
        }] : [],
        addOns: reservationData.addOns || [],
        notes: reservationData.notes || null,
        customPrice: reservationData.customPrice || null,
        paymentStatus: reservationData.paymentStatus || 'PENDING'
      };
    }
    
    const response = await api.post('/api/bookings/staff-reservation', requestData);
    return response;
  },

  // Add room to existing booking
  addRoomToBooking: async (bookingId, roomData) => {
    const response = await api.post(`/api/bookings/${bookingId}/rooms`, roomData);
    return response;
  },

  // Remove room from booking
  removeRoomFromBooking: async (bookingId, bookedRoomId) => {
    const response = await api.delete(`/api/bookings/${bookingId}/rooms/${bookedRoomId}`);
    return response;
  },

  // Add bed to existing booking
  addBedToBooking: async (bookingId, bedData) => {
    const response = await api.post(`/api/bookings/${bookingId}/beds`, bedData);
    return response;
  },

  // Remove bed from booking
  removeBedFromBooking: async (bookingId, bookedBedId) => {
    const response = await api.delete(`/api/bookings/${bookingId}/beds/${bookedBedId}`);
    return response;
  },

  // Add course to existing booking
  addCourseToBooking: async (bookingId, courseData) => {
    const response = await api.post(`/api/bookings/${bookingId}/courses`, courseData);
    return response;
  },

  // Remove course from booking
  removeCourseFromBooking: async (bookingId, bookedCourseId) => {
    const response = await api.delete(`/api/bookings/${bookingId}/courses/${bookedCourseId}`);
    return response;
  },

  // ===== DYNAMIC BOOKING MANAGEMENT API METHODS =====
  
  // Room Management
  updateBookedRoom: async (bookingId, bookedRoomId, updateData) => {
    const response = await api.patch(`/api/bookings/${bookingId}/rooms/${bookedRoomId}`, updateData);
    return response;
  },

  applyRoomDiscount: async (bookingId, bookedRoomId, discountData) => {
    const response = await api.post(`/api/bookings/${bookingId}/rooms/${bookedRoomId}/apply-discount`, discountData);
    return response;
  },

  reallocateRoom: async (bookingId, bookedRoomId, reallocationData) => {
    const response = await api.post(`/api/bookings/${bookingId}/rooms/${bookedRoomId}/reallocate`, reallocationData);
    return response;
  },

  // Bed Management
  updateBookedBed: async (bookingId, bookedBedId, updateData) => {
    const response = await api.patch(`/api/bookings/${bookingId}/beds/${bookedBedId}`, updateData);
    return response;
  },

  applyBedDiscount: async (bookingId, bookedBedId, discountData) => {
    const response = await api.post(`/api/bookings/${bookingId}/beds/${bookedBedId}/apply-discount`, discountData);
    return response;
  },

  reallocateBed: async (bookingId, bookedBedId, reallocationData) => {
    const response = await api.post(`/api/bookings/${bookingId}/beds/${bookedBedId}/reallocate`, reallocationData);
    return response;
  },

  // Course Management
  updateBookedCourse: async (bookingId, bookedCourseId, updateData) => {
    const response = await api.patch(`/api/bookings/${bookingId}/courses/${bookedCourseId}`, updateData);
    return response;
  },

  applyCourseDiscount: async (bookingId, bookedCourseId, discountData) => {
    const response = await api.post(`/api/bookings/${bookingId}/courses/${bookedCourseId}/apply-discount`, discountData);
    return response;
  },

  // ===== FLEXIBLE MODIFICATION API METHODS =====
  
  // Extend individual room or bed
  extendEntity: async (bookingId, data) => {
    const response = await api.post(`/api/bookings/${bookingId}/extend/entity`, data);
    return response;
  },

  // Extend all rooms and beds in booking (bulk)
  extendBulk: async (bookingId, data) => {
    const response = await api.post(`/api/bookings/${bookingId}/extend/bulk`, data);
    return response;
  },

  // Early check-in for individual room or bed
  earlyCheckinEntity: async (bookingId, data) => {
    const response = await api.post(`/api/bookings/${bookingId}/early-checkin/entity`, data);
    return response;
  },

  // Early check-in for all rooms and beds (bulk)
  earlyCheckinBulk: async (bookingId, data) => {
    const response = await api.post(`/api/bookings/${bookingId}/early-checkin/bulk`, data);
    return response;
  },

  // Early checkout for individual room or bed
  earlyCheckoutEntity: async (bookingId, data) => {
    const response = await api.post(`/api/bookings/${bookingId}/early-checkout/entity`, data);
    return response;
  },

  // Early checkout for all rooms and beds (bulk)
  earlyCheckoutBulk: async (bookingId, data) => {
    const response = await api.post(`/api/bookings/${bookingId}/early-checkout/bulk`, data);
    return response;
  },

  // Recalculate booking price (Admin+ only)
  recalculatePrice: async (bookingId) => {
    const response = await api.post(`/api/bookings/${bookingId}/recalculate-price`);
    return response;
  },

  recalculateDates: async (bookingId) => {
    const response = await api.post(`/api/bookings/${bookingId}/recalculate-dates`);
    return response;
  },

  // Get ID proofs for a booking
  getIdProofs: async (bookingId) => {
    const response = await api.get(`/api/bookings/${bookingId}/id-proof`);
    return response;
  },

  // Upload ID proof for a booking
  uploadIdProof: async (bookingId, formData) => {
    const response = await api.post(`/api/bookings/${bookingId}/upload-id-proof`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response;
  },

  // Delete ID proof image
  deleteIdProof: async (bookingId, imageId) => {
    const response = await api.delete(`/api/bookings/${bookingId}/id-proof/${imageId}`);
    return response;
  },
};

// Custom Price Breakdown Service
export const customPriceBreakdownService = {
  // Get all custom price breakdowns for a booking
  getByBooking: async (bookingId) => {
    const response = await api.get(`/api/bookings/custom-price-breakdowns/${bookingId}`);
    return response;
  },

  // Create custom price breakdowns for a booked item
  create: async (data) => {
    const response = await api.post('/api/bookings/custom-price-breakdowns', data);
    return response;
  },

  // Update a custom price breakdown
  update: async (breakdownId, data) => {
    const response = await api.put(`/api/bookings/custom-price-breakdowns/${breakdownId}`, data);
    return response;
  },

  // Delete a custom price breakdown
  delete: async (breakdownId) => {
    const response = await api.delete(`/api/bookings/custom-price-breakdowns/${breakdownId}`);
    return response;
  },
};

// Availability Service
export const availabilityService = {
  // Get all available rooms and beds for a booking period
  check: async (params) => {
    const response = await api.get('/api/availability', { params });
    return response;
  },

  // Get only available rooms for a booking period
  getRooms: async (params) => {
    const response = await api.get('/api/availability/rooms', { params });
    return response;
  },

  // Get only available beds for a booking period
  getBeds: async (params) => {
    const response = await api.get('/api/availability/beds', { params });
    return response;
  },

  // Legacy endpoint for backward compatibility
  checkLegacy: async (params) => {
    const response = await api.get('/api/availability/check', { params });
    return response;
  },

  // Check specific room availability
  checkRoom: async (roomId, params) => {
    const response = await api.get(`/api/availability/room/${roomId}`, { params });
    return response;
  },

  // Check specific bed availability
  checkBed: async (bedId, params) => {
    const response = await api.get(`/api/availability/bed/${bedId}`, { params });
    return response;
  },

  // Get occupancy report (staff+ only)
  getOccupancy: async (params) => {
    const response = await api.get('/api/availability/occupancy', { params });
    return response;
  },

  // Get calendar data (bookings + blocks) for a specific room
  getRoomCalendar: async (roomId, params = {}) => {
    try {
      // Try the full calendar endpoint first (for staff/admin users)
      const response = await api.get(`/api/availability/room/${roomId}/calendar`, { params });
      return response;
    } catch (error) {
      // If access denied (403) or unauthorized (401), try the public endpoint
      if (error.status === 403 || error.status === 401) {
        console.log('Falling back to public calendar endpoint...');
        const response = await api.get(`/api/availability/room/${roomId}/calendar/public`, { params });
        return response;
      }
      // Re-throw other errors
      throw error;
    }
  },
};

// Availability Blocks Service
export const availabilityBlocksService = {
  // Create a new availability block
  create: async (blockData) => {
    const response = await api.post('/api/availability-blocks', blockData);
    return response;
  },

  // Get all availability blocks with filtering
  getAll: async (params = {}) => {
    const response = await api.get('/api/availability-blocks', { params });
    return response;
  },

  // Get a specific availability block
  getById: async (id) => {
    const response = await api.get(`/api/availability-blocks/${id}`);
    return response;
  },

  // Update an availability block
  update: async (id, blockData) => {
    const response = await api.put(`/api/availability-blocks/${id}`, blockData);
    return response;
  },

  // Delete an availability block
  delete: async (id) => {
    await api.delete(`/api/availability-blocks/${id}`);
  },

  // Get all blocks for a specific room
  getRoomBlocks: async (roomId, includeInactive = false) => {
    const response = await api.get(`/api/availability-blocks/room/${roomId}`, {
      params: { includeInactive }
    });
    return response;
  },

  // Check if a room/bed is blocked during a period
  checkBlocked: async (params) => {
    const response = await api.post('/api/availability-blocks/check', params);
    return response;
  }
};

// Payments Service
export const paymentsService = {
  getAll: async (params = {}) => {
    const response = await api.get('/api/payments', { params });
    return response;
  },

  createIntent: async (paymentData) => {
    const response = await api.post('/api/payments/create-intent', paymentData);
    return response;
  },

  createManualPayment: async (paymentData) => {
    const response = await api.post('/api/payments/manual-create', paymentData);
    return response;
  },

  getByBooking: async (bookingId) => {
    const response = await api.get(`/api/payments/booking/${bookingId}`);
    return response;
  },

  processRefund: async (paymentId, amount) => {
    const response = await api.post(`/api/payments/${paymentId}/refund`, { amount });
    return response;
  },

  // Update payment record (admin only)
  updatePayment: async (paymentId, updateData) => {
    const response = await api.patch(`/api/payments/${paymentId}`, updateData);
    return response;
  },

  // Delete payment record (admin only)
  deletePayment: async (paymentId, deleteData) => {
    const response = await api.delete(`/api/payments/${paymentId}`, { data: deleteData });
    return response;
  },
};

// Refunds Service
export const refundsService = {
  getAll: async (params = {}) => {
    const response = await api.get('/api/refunds', { params });
    return response;
  },

  getById: async (id) => {
    const response = await api.get(`/api/refunds/${id}`);
    return response;
  },

  getByBooking: async (bookingId) => {
    const response = await api.get(`/api/refunds/booking/${bookingId}`);
    return response;
  },

  create: async (refundData) => {
    const response = await api.post('/api/refunds', refundData);
    return response;
  },

  update: async (id, refundData) => {
    const response = await api.put(`/api/refunds/${id}`, refundData);
    return response;
  },

  delete: async (id, deleteData) => {
    const response = await api.delete(`/api/refunds/${id}`, { data: deleteData });
    return response;
  },

  // Update refund record (admin only)
  updateRefund: async (refundId, updateData) => {
    const response = await api.patch(`/api/refunds/${refundId}`, updateData);
    return response;
  },
};

// Users Service
export const usersService = {
  // Get all users
  getAll: async (params = {}) => {
    const response = await api.get('/api/users', { params });
    return response;
  },

  // Get user by ID
  getById: async (id) => {
    const response = await api.get(`/api/users/${id}`);
    return response;
  },

  // Create new user (PROPERTYADMIN, SUPERADMIN only)
  createUser: async (userData) => {
    const response = await api.post('/api/users/create', userData);
    return response;
  },

  // Update user role (PROPERTYADMIN, SUPERADMIN only)
  updateUserRole: async (userId, newRole) => {
    const response = await api.put(`/api/users/${userId}/role`, { role: newRole });
    return response;
  },

  // Admin reset user password (PROPERTYADMIN, SUPERADMIN only)
  resetUserPassword: async (userId, newPassword) => {
    const response = await api.put(`/api/users/${userId}/password`, { newPassword });
    return response;
  },

  // User change own password (All authenticated users)
  changeOwnPassword: async (currentPassword, newPassword) => {
    const response = await api.post('/api/users/change-password', { currentPassword, newPassword });
    return response;
  },

  // Update user profile (All authenticated users for own profile, Admins for others)
  updateUserProfile: async (userId, profileData) => {
    const response = await api.put(`/api/users/${userId}/profile`, profileData);
    return response;
  },

  // Own account (signed-in user): profile, email and password
  getMyProfile: async () => {
    const response = await api.get('/api/users/me');
    return response;
  },

  updateMyProfile: async (profileData) => {
    const response = await api.put('/api/users/me/profile', profileData);
    return response;
  },

  // Returns { user, token }: the new token carries the new email
  changeMyEmail: async ({ newEmail, currentPassword }) => {
    const response = await api.put('/api/users/me/email', { newEmail, currentPassword });
    return response;
  },

  changeMyPassword: async ({ currentPassword, newPassword, confirmPassword }) => {
    const response = await api.put('/api/users/me/password', { currentPassword, newPassword, confirmPassword });
    return response;
  },

  // Toggle user verification (PROPERTYADMIN, SUPERADMIN only)
  toggleUserVerification: async (userId) => {
    const response = await api.put(`/api/users/${userId}/toggle-verify`);
    return response;
  },

  // Get allowed roles for current user (PROPERTYADMIN, SUPERADMIN only)
  getAllowedRoles: async () => {
    const response = await api.get('/api/users/roles/allowed');
    return response;
  },

  // Soft delete user (SUPERADMIN only)
  deleteUser: async (userId) => {
    const response = await api.delete(`/api/users/${userId}`);
    return response;
  },

  // Search users
  search: async (searchData) => {
    const response = await api.post('/api/users/search', searchData);
    return response;
  },

  // Get user bookings
  getUserBookings: async (userId) => {
    const response = await api.get(`/api/users/${userId}/bookings`);
    return response;
  },

  // Legacy endpoints (kept for backward compatibility)
  create: async (userData) => {
    // Use new createUser endpoint
    return await usersService.createUser(userData);
  },

  update: async (id, userData) => {
    // Use new updateUserProfile endpoint
    return await usersService.updateUserProfile(id, userData);
  },

  delete: async (id) => {
    // Use new deleteUser endpoint
    return await usersService.deleteUser(id);
  },
};

// Night Audit Service
export const nightAuditService = {
  // Get current audit with step info (NEW SYSTEM)
  getCurrentAudit: async () => {
    const response = await api.get('/api/night-audit/current');
    return response.data;
  },

  // Refresh current step data (NEW SYSTEM)
  refreshStepData: async () => {
    const response = await api.post('/api/night-audit/step/refresh');
    return response.data;
  },

  // Complete current step (NEW SYSTEM)
  completeStep: async (step, verificationData = null) => {
    const response = await api.post('/api/night-audit/step/complete', {
      step,
      verificationData,
    });
    return response.data;
  },

  // Create a new night audit
  create: async (auditData) => {
    const response = await api.post('/api/night-audit', auditData);
    return response;
  },

  // Get all night audits with pagination
  getAll: async (params = {}) => {
    const response = await api.get('/api/night-audit', { params });
    return response;
  },

  // Get night audit by date
  getByDate: async (date) => {
    const response = await api.get(`/api/night-audit/date/${date}`);
    return response;
  },

  // Update night audit
  update: async (id, auditData) => {
    const response = await api.patch(`/api/night-audit/${id}`, auditData);
    return response;
  },

  // Complete night audit (shortcut)
  complete: async (id) => {
    const response = await api.post(`/api/night-audit/${id}/complete`);
    return response;
  },

  // Reopen night audit (Super Admin only)
  reopen: async (id) => {
    const response = await api.post(`/api/night-audit/${id}/reopen`);
    return response;
  },

  // Get night audit statistics
  getStats: async (params = {}) => {
    const response = await api.get('/api/night-audit/stats', { params });
    return response;
  },

  // Admin: Get all night audits (no pagination)
  getAllAudits: async () => {
    const response = await api.get('/api/night-audit/admin/all');
    return response.data;
  },

  // Admin: Delete night audit
  deleteAudit: async (id) => {
    const response = await api.delete(`/api/night-audit/admin/${id}`);
    return response;
  },
};

// Audit Service
export const auditService = {
  getAll: async (params = {}) => {
    const response = await api.get('/api/audit', { params });
    return response;
  },

  getById: async (id) => {
    const response = await api.get(`/api/audit/${id}`);
    return response;
  },

  getStats: async (params = {}) => {
    const response = await api.get('/api/audit/stats', { params });
    return response;
  },

  // Get audit logs for a specific booking
  getByBooking: async (bookingId, params = {}) => {
    const response = await api.get(`/api/audit/booking/${bookingId}`, { params });
    return response;
  },

  // Get audit logs for a specific room
  getByRoom: async (roomId, params = {}) => {
    const response = await api.get(`/api/audit/room/${roomId}`, { params });
    return response;
  },
};

// Health Service
export const healthService = {
  check: async () => {
    const response = await api.get('/health');
    return response;
  },
};

// Pricing Service
export const pricingService = {
  // Get pricing overview for admin dashboard
  getPricingOverview: () => {
    return api.get('/api/pricing/overview');
  },

  // Get dynamic prices for a specific room type
  getDynamicPricesForRoomType: (roomTypeId) => {
    return api.get(`/api/pricing/room-type/${roomTypeId}`);
  },

  // Calculate price for a date range
  calculatePrice: (data) => {
    return api.post('/api/pricing/calculate', data);
  },

  // Create new dynamic price
  createDynamicPrice: (data) => {
    return api.post('/api/pricing', data);
  },

  // Update dynamic price
  updateDynamicPrice: (id, data) => {
    return api.put(`/api/pricing/${id}`, data);
  },

  // Delete dynamic price
  deleteDynamicPrice: (id) => {
    return api.delete(`/api/pricing/${id}`);
  },

  // Update room type base price
  updateBasePrice: (roomTypeId, basePrice) => {
    return api.put(`/api/pricing/room-type/${roomTypeId}/base-price`, { basePrice });
  }
};

// Image Service
export const imageService = {
  // Upload single image
  uploadSingle: async (formData) => {
    const response = await api.post('/api/images/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response;
  },

  // Upload multiple images
  uploadMultiple: async (formData) => {
    const response = await api.post('/api/images/upload-multiple', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response;
  },

  // Get images for an entity
  getImages: async (entityType, entityId) => {
    const response = await api.get(`/api/images/${entityType}/${entityId}`);
    // Debug: Log the response to see what URLs are returned
    console.log(`Images for ${entityType} ${entityId}:`, response);
    return response;
  },

  // Update image description
  updateImage: async (imageId, description) => {
    const response = await api.put(`/api/images/${imageId}`, { description });
    return response;
  },

  // Delete single image
  deleteImage: async (imageId) => {
    await api.delete(`/api/images/${imageId}`);
  },

  // Delete all images for entity
  deleteEntityImages: async (entityType, entityId) => {
    await api.delete(`/api/images/${entityType}/${entityId}`);
  },

  // Test image URL accessibility
  testImageUrl: async (url) => {
    try {
      const response = await fetch(url, { method: 'HEAD' });
      return {
        accessible: response.ok,
        status: response.status,
        headers: Object.fromEntries(response.headers.entries())
      };
    } catch (error) {
      return {
        accessible: false,
        error: error.message
      };
    }
  },
};

// ID Proof Image Service
export const idProofImageService = {
  // Upload ID proof image
  upload: async (formData) => {
    const response = await api.post('/api/id-proof-images/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response;
  },

  // Get ID proof images for a user
  getByUser: async (userId) => {
    const response = await api.get(`/api/id-proof-images/user/${userId}`);
    // Debug: Log the response to see what URLs are returned
    console.log(`ID proof images for user ${userId}:`, response);
    return response;
  },

  // Update image description
  updateDescription: async (imageId, description) => {
    const response = await api.put(`/api/id-proof-images/${imageId}/description`, { description });
    return response;
  },

  // Delete ID proof image
  delete: async (imageId) => {
    await api.delete(`/api/id-proof-images/${imageId}`);
  },
};

// Invoice Service
export const invoiceService = {
  // Download standard invoice PDF for a booking
  downloadInvoice: async (bookingId, companyDetails = {}) => {
    try {
      // Build query parameters for company details if provided
      const queryParams = new URLSearchParams();
      
      if (companyDetails?.companyName) {
        queryParams.append('companyName', companyDetails.companyName);
      }
      if (companyDetails?.gstNumber) {
        queryParams.append('gstNumber', companyDetails.gstNumber);
      }
      
      // Build the URL with query parameters
      const queryString = queryParams.toString();
      const url = `${API_CONFIG.INVOICE_URL}/api/invoice/download/${bookingId}${queryString ? `?${queryString}` : ''}`;
      
      console.log('Downloading invoice from:', url);
      
      // Use GET endpoint with query parameters
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Accept': 'application/pdf',
        },
      });
      
      if (!response.ok) {
        throw new Error(`Failed to download invoice: ${response.statusText}`);
      }
      
      // Ensure we're getting a PDF
      const contentType = response.headers.get('content-type');
      console.log('Response content-type:', contentType);
      
      const blob = await response.blob();
      console.log('Blob size:', blob.size, 'Blob type:', blob.type);
      
      // Create a proper PDF blob if content-type is not set
      const pdfBlob = blob.type === 'application/pdf' ? blob : new Blob([blob], { type: 'application/pdf' });
      
      const url_download = window.URL.createObjectURL(pdfBlob);
      const link = document.createElement('a');
      link.href = url_download;
      link.download = `invoice-${bookingId}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url_download);
      
      return { success: true, message: 'Invoice downloaded successfully' };
    } catch (error) {
      console.error('Invoice download error:', error);
      throw error;
    }
  },

  // Generate flexible invoice for a booking
  generateFlexibleInvoice: async (bookingId, options = {}) => {
    try {
      const response = await fetch(`${API_CONFIG.INVOICE_URL}/api/invoice/flexible/generate/${bookingId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify({
          includeRoom: options.includeRoom ?? true,
          includeCourse: options.includeCourse ?? true,
          includeAddons: options.includeAddons ?? true,
          includePayments: options.includePayments ?? true,
          companyName: options.companyName || undefined,
          gstNumber: options.gstNumber || undefined
        })
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `Failed to generate flexible invoice: ${response.statusText}`);
      }
      
      const result = await response.json();
      return result;
    } catch (error) {
      console.error('Flexible invoice generation error:', error);
      throw error;
    }
  },

  // Download flexible invoice PDF for a booking
  downloadFlexibleInvoice: async (bookingId, options = {}) => {
    try {
      // Check if company details are provided
      const hasCompanyDetails = options?.companyName || options?.gstNumber;
      
      if (hasCompanyDetails) {
        // Use new POST endpoint with company details
        const response = await fetch(`${API_CONFIG.INVOICE_URL}/api/invoice/flexible/generate/${bookingId}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Accept': 'application/pdf',
          },
          body: JSON.stringify({
            includeRoom: options.includeRoom ?? true,
            includeCourse: options.includeCourse ?? true,
            includeAddons: options.includeAddons ?? true,
            includePayments: options.includePayments ?? true,
            companyName: options.companyName || undefined,
            gstNumber: options.gstNumber || undefined
          })
        });
        
        if (!response.ok) {
          throw new Error(`Failed to download flexible invoice: ${response.statusText}`);
        }
        
        // Ensure we're getting a PDF
        const contentType = response.headers.get('content-type');
        console.log('Flexible Invoice Response content-type:', contentType);
        
        const blob = await response.blob();
        console.log('Flexible Invoice Blob size:', blob.size, 'Blob type:', blob.type);
        
        // Create a proper PDF blob if content-type is not set
        const pdfBlob = blob.type === 'application/pdf' ? blob : new Blob([blob], { type: 'application/pdf' });
        
        const url = window.URL.createObjectURL(pdfBlob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `invoice-flexible-${bookingId}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        
        return { success: true, message: 'Flexible invoice downloaded successfully' };
      } else {
        // Use old GET endpoint with query params for backward compatibility
        const params = new URLSearchParams({
          includeRoom: (options.includeRoom ?? true).toString(),
          includeCourse: (options.includeCourse ?? true).toString(),
          includeAddons: (options.includeAddons ?? true).toString(),
          includePayments: (options.includePayments ?? true).toString()
        });

        const response = await fetch(`${API_CONFIG.INVOICE_URL}/api/invoice/flexible/download/${bookingId}?${params}`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Accept': 'application/pdf',
          },
        });
        
        if (!response.ok) {
          throw new Error(`Failed to download flexible invoice: ${response.statusText}`);
        }
        
        const blob = await response.blob();
        const pdfBlob = blob.type === 'application/pdf' ? blob : new Blob([blob], { type: 'application/pdf' });
        const url = window.URL.createObjectURL(pdfBlob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `invoice-flexible-${bookingId}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        
        return { success: true, message: 'Flexible invoice downloaded successfully' };
      }
    } catch (error) {
      console.error('Flexible invoice download error:', error);
      throw error;
    }
  },

  // Generate flexible invoice by booking number
  generateFlexibleInvoiceByNumber: async (bookingNumber, options = {}) => {
    try {
      const response = await fetch(`${API_CONFIG.INVOICE_URL}/api/invoice/flexible/generate/booking-number/${bookingNumber}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify({
          includeRoom: options.includeRoom ?? true,
          includeCourse: options.includeCourse ?? true,
          includeAddons: options.includeAddons ?? true,
          includePayments: options.includePayments ?? true,
          companyName: options.companyName || undefined,
          gstNumber: options.gstNumber || undefined
        })
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `Failed to generate flexible invoice: ${response.statusText}`);
      }
      
      const result = await response.json();
      return result;
    } catch (error) {
      console.error('Flexible invoice generation error:', error);
      throw error;
    }
  },

  // Download flexible invoice PDF by booking number
  downloadFlexibleInvoiceByNumber: async (bookingNumber, options = {}) => {
    try {
      const response = await fetch(`${API_CONFIG.INVOICE_URL}/api/invoice/flexible/generate/booking-number/${bookingNumber}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify({
          includeRoom: options.includeRoom ?? true,
          includeCourse: options.includeCourse ?? true,
          includeAddons: options.includeAddons ?? true,
          includePayments: options.includePayments ?? true,
          companyName: options.companyName || undefined,
          gstNumber: options.gstNumber || undefined
        })
      });
      
      if (!response.ok) {
        throw new Error(`Failed to download flexible invoice: ${response.statusText}`);
      }
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `invoice-flexible-${bookingNumber}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      return { success: true, message: 'Flexible invoice downloaded successfully' };
    } catch (error) {
      console.error('Flexible invoice download error:', error);
      throw error;
    }
  },

  // Generate standard invoice for a booking
  generateInvoice: async (bookingId, companyDetails = {}) => {
    try {
      const response = await fetch(`${API_CONFIG.INVOICE_URL}/api/invoice/generate/${bookingId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify({
          companyName: companyDetails.companyName || undefined,
          gstNumber: companyDetails.gstNumber || undefined
        })
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `Failed to generate invoice: ${response.statusText}`);
      }
      
      const result = await response.json();
      return result;
    } catch (error) {
      console.error('Invoice generation error:', error);
      throw error;
    }
  },

  // Generate standard invoice by booking number
  generateInvoiceByNumber: async (bookingNumber, companyDetails = {}) => {
    try {
      const response = await fetch(`${API_CONFIG.INVOICE_URL}/api/invoice/generate/booking-number/${bookingNumber}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify({
          companyName: companyDetails.companyName || undefined,
          gstNumber: companyDetails.gstNumber || undefined
        })
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `Failed to generate invoice: ${response.statusText}`);
      }
      
      const result = await response.json();
      return result;
    } catch (error) {
      console.error('Invoice generation error:', error);
      throw error;
    }
  },

  // Download standard invoice by booking number
  downloadInvoiceByNumber: async (bookingNumber, companyDetails = {}) => {
    try {
      const response = await fetch(`${API_CONFIG.INVOICE_URL}/api/invoice/generate/booking-number/${bookingNumber}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify({
          companyName: companyDetails.companyName || undefined,
          gstNumber: companyDetails.gstNumber || undefined
        })
      });
      
      if (!response.ok) {
        throw new Error(`Failed to download invoice: ${response.statusText}`);
      }
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `invoice-${bookingNumber}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      return { success: true, message: 'Invoice downloaded successfully' };
    } catch (error) {
      console.error('Invoice download error:', error);
      throw error;
    }
  },
};

// Email Service for sending invoices
export const emailService = {
  // Send invoice email to booking's default email
  sendInvoiceEmail: async (bookingId) => {
    try {
      const response = await fetch(`${API_CONFIG.INVOICE_URL}/api/email/send-invoice`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify({ bookingId }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to send invoice email');
      }

      return data;
    } catch (error) {
      console.error('Error sending invoice email:', error);
      throw error;
    }
  },

  // Send custom invoice email with custom recipient and content
  sendCustomInvoiceEmail: async ({ bookingId, email, subject, htmlContent }) => {
    try {
      const response = await fetch(`${API_CONFIG.INVOICE_URL}/api/email/send-custom-invoice`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify({
          bookingId,
          email,
          subject,
          htmlContent,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to send custom invoice email');
      }

      return data;
    } catch (error) {
      console.error('Error sending custom invoice email:', error);
      throw error;
    }
  },

  // Get email queue status
  getQueueStatus: async () => {
    try {
      const response = await fetch(`${API_CONFIG.INVOICE_URL}/api/email/queue-status`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to get queue status');
      }

      return data;
    } catch (error) {
      console.error('Error getting queue status:', error);
      throw error;
    }
  },

  // Get booking email audit logs
  getBookingEmailLogs: async (bookingId) => {
    try {
      const response = await fetch(`${API_CONFIG.INVOICE_URL}/api/email/audit-logs/${bookingId}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to get email logs');
      }

      return data;
    } catch (error) {
      console.error('Error getting email logs:', error);
      throw error;
    }
  },

  // Get recent email audit logs
  getRecentEmailLogs: async (limit = 100) => {
    try {
      const response = await fetch(`${API_CONFIG.INVOICE_URL}/api/email/audit-logs?limit=${limit}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to get recent email logs');
      }

      return data;
    } catch (error) {
      console.error('Error getting recent email logs:', error);
      throw error;
    }
  },

  // Get email statistics
  getEmailStats: async (startDate, endDate) => {
    try {
      const params = new URLSearchParams();
      if (startDate) params.append('startDate', startDate);
      if (endDate) params.append('endDate', endDate);
      
      const url = `${API_CONFIG.INVOICE_URL}/api/email/stats${params.toString() ? `?${params.toString()}` : ''}`;
      
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to get email stats');
      }

      return data;
    } catch (error) {
      console.error('Error getting email stats:', error);
      throw error;
    }
  },
};

// Super Admin Service
export const superAdminService = {
  // Update booking and user data
  updateBookingAndUser: async (bookingId, data) => {
    const response = await api.put(`/api/super-admin/update-booking/${bookingId}`, data);
    return response;
  },

  // Get booking audit trail
  getBookingAudit: async (bookingId) => {
    const response = await api.get(`/api/super-admin/booking-audit/${bookingId}`);
    return response;
  },

  // Get user bookings
  getUserBookings: async (userId, params = {}) => {
    const response = await api.get(`/api/super-admin/user-bookings/${userId}`, { params });
    return response;
  },

  // Check past date availability
  checkPastDateAvailability: async (roomId, checkIn, checkOut) => {
    const params = new URLSearchParams({
      checkIn: new Date(checkIn).toISOString(),
      checkOut: new Date(checkOut).toISOString()
    });
    const response = await api.get(`/api/super-admin/past-availability/${roomId}?${params}`);
    return response;
  },

  // Create past date booking
  createPastDateBooking: async (data) => {
    // Transform old format to new format if needed
    let requestData = data;
    
    // Check if it's old format (has roomId, bedId, or courseId at root level)
    if (data.roomId || data.bedId || data.courseId) {
      requestData = {
        guestDetails: data.guestDetails,
        checkIn: data.checkIn,
        checkOut: data.checkOut,
        rooms: data.roomId ? [{
          roomId: data.roomId,
          checkIn: data.checkIn,
          checkOut: data.checkOut
        }] : [],
        beds: data.bedId ? [{
          bedId: data.bedId,
          checkIn: data.checkIn,
          checkOut: data.checkOut
        }] : [],
        courses: data.courseId ? [{
          courseId: data.courseId,
          quantity: data.courseCount || 1
        }] : [],
        paymentStatus: data.paymentStatus || 'SUCCESS',
        customPrice: data.customPrice || null,
        notes: data.notes || null,
        reason: data.reason || null,
        adminNote: data.adminNote || null
      };
    }
    
    const response = await api.post('/api/super-admin/past-booking', requestData);
    return response;
  },

  // Early check-in availability check
  checkEarlyCheckinAvailability: async (bookingId, newCheckInDate) => {
    const params = new URLSearchParams({
      bookingId,
      newCheckInDate: new Date(newCheckInDate).toISOString()
    });
    const response = await api.get(`/api/super-admin/early-checkin-availability?${params}`);
    return response;
  },

  // Process early check-in
  processEarlyCheckin: async (data) => {
    const response = await api.post('/api/super-admin/early-checkin', data);
    return response;
  }
};

// Debug utilities for image troubleshooting
export const debugUtils = {
  // Test image URL accessibility
  testImageUrl: async (url) => {
    console.log(`Testing image URL: ${url}`);
    
    try {
      // Test with HEAD request first
      const headResponse = await fetch(url, { method: 'HEAD' });
      console.log(`HEAD response for ${url}:`, {
        status: headResponse.status,
        ok: headResponse.ok,
        headers: Object.fromEntries(headResponse.headers.entries())
      });

      // Test with GET request to ensure it's actually loadable
      const getResponse = await fetch(url);
      console.log(`GET response for ${url}:`, {
        status: getResponse.status,
        ok: getResponse.ok,
        size: getResponse.headers.get('content-length'),
        type: getResponse.headers.get('content-type')
      });

      // Test with Image constructor
      const img = new Image();
      const imageTest = new Promise((resolve, reject) => {
        img.onload = () => {
          console.log(`Image constructor test passed for ${url}:`, {
            width: img.naturalWidth,
            height: img.naturalHeight
          });
          resolve(true);
        };
        img.onerror = (error) => {
          console.error(`Image constructor test failed for ${url}:`, error);
          reject(error);
        };
        img.src = url;
      });

      await imageTest;
      return { success: true, url };
    } catch (error) {
      console.error(`Image URL test failed for ${url}:`, error);
      return { success: false, url, error: error.message };
    }
  },

  // Test all images for a user
  testUserImages: async (userId) => {
    console.log(`Testing all images for user ${userId}`);
    
    try {
      const response = await idProofImageService.getByUser(userId);
      const images = response.data || [];
      
      console.log(`Found ${images.length} images for user ${userId}`);
      
      const results = [];
      for (const image of images) {
        const result = await debugUtils.testImageUrl(image.url);
        results.push({ ...result, imageId: image.id, idType: image.idType });
      }
      
      console.log(`Test results for user ${userId}:`, results);
      return results;
    } catch (error) {
      console.error(`Failed to test images for user ${userId}:`, error);
      return [];
    }
  },

  // Test all images for a room
  testRoomImages: async (roomId) => {
    console.log(`Testing all images for room ${roomId}`);
    
    try {
      const response = await imageService.getImages('room', roomId);
      const images = response.data || [];
      
      console.log(`Found ${images.length} images for room ${roomId}`);
      
      const results = [];
      for (const image of images) {
        const result = await debugUtils.testImageUrl(image.url);
        results.push({ ...result, imageId: image.id, description: image.description });
      }
      
      console.log(`Test results for room ${roomId}:`, results);
      return results;
    } catch (error) {
      console.error(`Failed to test images for room ${roomId}:`, error);
      return [];
    }
  },

  // Test API endpoints
  testImageApis: async () => {
    console.log('Testing image API endpoints');
    
    const tests = [
      { name: 'User ID Proof API', test: () => idProofImageService.getByUser(1) },
      { name: 'Room Images API', test: () => imageService.getImages('room', 1) },
    ];

    for (const testCase of tests) {
      try {
        console.log(`Testing ${testCase.name}...`);
        const result = await testCase.test();
        console.log(`${testCase.name} success:`, result);
      } catch (error) {
        console.error(`${testCase.name} failed:`, error);
      }
    }
  }
};

// Price Breakdowns Service
export const priceBreakdownsService = {
  // Get all price breakdowns with pagination
  getAll: async (params = {}) => {
    const response = await api.get('/api/price-breakdowns', { params });
    return response;
  },

  // Get specific price breakdown by ID
  getById: async (id) => {
    const response = await api.get(`/api/price-breakdowns/${id}`);
    return response;
  },

  // Get price breakdowns for specific room type
  getRoomTypeBreakdowns: async (roomTypeId, includeInactive = false) => {
    const response = await api.get(`/api/price-breakdowns/roomtype/${roomTypeId}`, {
      params: { includeInactive }
    });
    return response;
  },

  // Create breakdown for room type
  createRoomTypeBreakdown: async (data) => {
    const response = await api.post('/api/price-breakdowns/roomtype', data);
    return response;
  },

  // Get price breakdowns for course
  getCourseBreakdowns: async (courseId, includeInactive = false) => {
    const response = await api.get(`/api/price-breakdowns/course/${courseId}`, {
      params: { includeInactive }
    });
    return response;
  },

  // Create breakdown for course
  createCourseBreakdown: async (data) => {
    const response = await api.post('/api/price-breakdowns/course', data);
    return response;
  },

  // Get price breakdowns for add-on service
  getAddOnServiceBreakdowns: async (addOnServiceId, includeInactive = false) => {
    const response = await api.get(`/api/price-breakdowns/addon-service/${addOnServiceId}`, {
      params: { includeInactive }
    });
    return response;
  },

  // Create breakdown for add-on service
  createAddOnServiceBreakdown: async (data) => {
    const response = await api.post('/api/price-breakdowns/addon-service', data);
    return response;
  },

  // Create new price breakdown (legacy)
  create: async (breakdownData) => {
    const response = await api.post('/api/price-breakdowns', breakdownData);
    return response;
  },

  // Update price breakdown
  update: async (id, breakdownData) => {
    const response = await api.patch(`/api/price-breakdowns/${id}`, breakdownData);
    return response;
  },

  // Delete price breakdown
  delete: async (id) => {
    const response = await api.delete(`/api/price-breakdowns/${id}`);
    return response;
  }
};

// Web Check-in Service (Staff endpoints)
export const webCheckinService = {
  // List all web check-in forms with filters
  listForms: async (params = {}) => {
    try {
      const queryString = new URLSearchParams(params).toString();
      const response = await fetch(`${API_CONFIG.INVOICE_URL}/api/staff/checkin/forms${queryString ? `?${queryString}` : ''}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        throw new Error(`Failed to fetch forms: ${response.statusText}`);
      }
      
      return await response.json();
    } catch (error) {
      console.error('List forms error:', error);
      throw error;
    }
  },

  // Get form details by booking ID
  getFormByBooking: async (bookingId) => {
    try {
      const response = await fetch(`${API_CONFIG.INVOICE_URL}/api/staff/checkin/booking/${bookingId}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        throw new Error(`Failed to fetch form details: ${response.statusText}`);
      }
      
      return await response.json();
    } catch (error) {
      console.error('Get form details error:', error);
      throw error;
    }
  },

  // Update check-in form data
  updateFormByBooking: async (bookingId, data) => {
    try {
      const response = await fetch(`${API_CONFIG.INVOICE_URL}/api/staff/checkin/booking/${bookingId}`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(data)
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `Failed to update form: ${response.statusText}`);
      }
      
      return await response.json();
    } catch (error) {
      console.error('Update form error:', error);
      throw error;
    }
  },

  // Verify payment
  verifyPayment: async (bookingId, data) => {
    try {
      const response = await fetch(`${API_CONFIG.INVOICE_URL}/api/staff/checkin/booking/${bookingId}/payment/verify`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(data)
      });
      
      if (!response.ok) {
        throw new Error(`Failed to verify payment: ${response.statusText}`);
      }
      
      return await response.json();
    } catch (error) {
      console.error('Verify payment error:', error);
      throw error;
    }
  },

  // Verify document
  verifyDocument: async (bookingId, data) => {
    try {
      const response = await fetch(`${API_CONFIG.INVOICE_URL}/api/staff/checkin/booking/${bookingId}/document/verify`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(data)
      });
      
      if (!response.ok) {
        throw new Error(`Failed to verify document: ${response.statusText}`);
      }
      
      return await response.json();
    } catch (error) {
      console.error('Verify document error:', error);
      throw error;
    }
  },

  // Get statistics
  getStatistics: async (params = {}) => {
    try {
      const queryString = new URLSearchParams(params).toString();
      const response = await fetch(`${API_CONFIG.INVOICE_URL}/api/staff/checkin/statistics${queryString ? `?${queryString}` : ''}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        throw new Error(`Failed to fetch statistics: ${response.statusText}`);
      }
      
      return await response.json();
    } catch (error) {
      console.error('Get statistics error:', error);
      throw error;
    }
  },

  // Bulk verify payments (Admin only)
  bulkVerifyPayments: async (data) => {
    try {
      const response = await fetch(`${API_CONFIG.INVOICE_URL}/api/staff/checkin/payments/bulk-verify`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(data)
      });
      
      if (!response.ok) {
        throw new Error(`Failed to bulk verify payments: ${response.statusText}`);
      }
      
      return await response.json();
    } catch (error) {
      console.error('Bulk verify payments error:', error);
      throw error;
    }
  },

  // Delete check-in form (SuperAdmin only)
  deleteForm: async (formId) => {
    try {
      const response = await fetch(`${API_CONFIG.INVOICE_URL}/api/staff/checkin/form/${formId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.error || `Failed to delete form: ${response.statusText}`);
      }
      
      return data;
    } catch (error) {
      console.error('Delete form error:', error);
      throw error;
    }
  },

  // Get pending payment verifications
  getPendingPayments: async (params = {}) => {
    try {
      const queryString = new URLSearchParams(params).toString();
      const response = await fetch(`${API_CONFIG.INVOICE_URL}/api/staff/checkin/payments/pending${queryString ? `?${queryString}` : ''}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        throw new Error(`Failed to fetch pending payments: ${response.statusText}`);
      }
      
      return await response.json();
    } catch (error) {
      console.error('Get pending payments error:', error);
      throw error;
    }
  }
};

// Make debug utils available globally for console access
window.imageDebugUtils = debugUtils;

// Reports Service - New comprehensive reporting endpoints
export const reportsService = {
  // Get list of all available report types
  getReportTypes: async () => {
    const response = await api.get('/api/reports/types');
    return response;
  },

  // Master Booking Report
  getMasterBooking: async (params = {}) => {
    const response = await api.get('/api/reports/master-booking', { params });
    return response;
  },

  // Occupancy Report
  getOccupancy: async (params = {}) => {
    const response = await api.get('/api/reports/occupancy', { params });
    return response;
  },

  // Course Participants Report
  getCourseParticipants: async (params = {}) => {
    const response = await api.get('/api/reports/course-participants', { params });
    return response;
  },

  // Accounts Report with GST Breakdown
  getAccounts: async (params = {}) => {
    const response = await api.get('/api/reports/accounts', { params });
    return response;
  },

  // Check-in Report
  getCheckIn: async (params = {}) => {
    const response = await api.get('/api/reports/check-in', { params });
    return response;
  },

  // Check-out Report
  getCheckOut: async (params = {}) => {
    const response = await api.get('/api/reports/check-out', { params });
    return response;
  },

  // OMG Experience Report
  getOmgExperience: async (params = {}) => {
    const response = await api.get('/api/reports/omg-experience', { params });
    return response;
  },

  // Daily Summary Report
  getDailySummary: async (params = {}) => {
    const response = await api.get('/api/reports/daily-summary', { params });
    return response;
  },

  // Services & Inventory Report
  getServicesInventory: async (params = {}) => {
    const response = await api.get('/api/reports/services-inventory', { params });
    return response;
  },

  // Custom Dynamic Report - Schema Discovery
  getSchema: async () => {
    const response = await api.get('/api/reports/schema');
    return response;
  },

  // Custom Dynamic Report - Generate Custom Report
  getCustomReport: async (queryConfig) => {
    const response = await api.post('/api/reports/custom', queryConfig);
    return response;
  },

  // Monthly Occupancy Report
  getMonthlyOccupancy: async (params = {}) => {
    const response = await api.get('/api/reports/occupancy/monthly', { params });
    return response;
  },

  // Yearly Occupancy Report
  getYearlyOccupancy: async (params = {}) => {
    const response = await api.get('/api/reports/occupancy/yearly', { params });
    return response;
  },

  // Payment Report
  getPayments: async (params = {}) => {
    const response = await api.get('/api/reports/payments', { params });
    return response;
  },

  // Accounts Master Report (flat addon columns for spreadsheet tax analysis)
  getAccountsMaster: async (params = {}) => {
    const response = await api.get('/api/reports/accounts-master', { params });
    return response;
  }
};

// Reconciliation Service
export const reconciliationService = {
  getDashboardSummary: async () => {
    const response = await api.get('/api/reconciliation/dashboard/summary');
    return response;
  },

  getPaymentIssues: async (params = {}) => {
    const response = await api.get('/api/reconciliation/payments/issues', { params });
    return response;
  },

  getPaymentIssueDetail: async (id) => {
    const response = await api.get(`/api/reconciliation/payments/issues/${encodeURIComponent(id)}`);
    return response;
  },

  getLogs: async (params = {}) => {
    const response = await api.get('/api/reconciliation/logs', { params });
    return response;
  },

  getUnreconciledPayments: async (params = {}) => {
    const response = await api.get('/api/reconciliation/payments/unreconciled', { params });
    return response;
  },

  getAmountMismatches: async (params = {}) => {
    const response = await api.get('/api/reconciliation/payments/mismatches', { params });
    return response;
  },

  triggerReconciliation: async (body) => {
    const response = await api.post('/api/reconciliation/trigger', body);
    return response;
  },

  getStatus: async () => {
    const response = await api.get('/api/reconciliation/status');
    return response;
  },
};

// System Kill Switch Service (SUPERADMIN only)
export const killSwitchService = {
  // Public status — reachable even while the system is paused
  getStatus: async () => {
    const response = await api.get('/api/system/status');
    return response;
  },

  // Full switch state for the control panel
  get: async () => {
    const response = await api.get('/api/system/kill-switch');
    return response;
  },

  // Apply both checkboxes in one call; clearing both restores the platform
  update: async (payload) => {
    const response = await api.put('/api/system/kill-switch', payload);
    return response;
  },

  // Convenience: bring everything back online
  restore: async (notifyStaff = true) => {
    const response = await api.post('/api/system/kill-switch/restore', { notifyStaff });
    return response;
  },

  // Audit trail: who activated/restored the switch and when (SUPERADMIN only)
  history: async (params = {}) => {
    const response = await api.get('/api/system/kill-switch/history', { params });
    return response;
  },
};

// OMG Experience Service
export { default as omgExperienceService } from './omgExperienceService';

export default api;